import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Components
import axiosInstance from '../components/axios';
import {
	validPasswordLength,
	validPasswordUppercase,
	validPasswordLowercase,
	validPasswordNumber,
	validPasswordSpecial,
	validPasswordNotEmail
} from './validation';

// Static resources
import bgimage from '../images/login-page.jpg';
import Toast from './toast';

//MaterialUI
import {
	Box,
	Button,
	CssBaseline,
	List,
	ListItem,
	ListItemText,
	TextField,
	Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import Avatar from '@mui/material/Avatar';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import PinIcon from '@mui/icons-material/Pin';

export default function NewPassword() {

	const navigate = useNavigate();
	let [searchParams] = useSearchParams();

	// Next page to redirect to after login
	const [email, setEmail] = useState(searchParams.get('e') || '');
	const [resetToken, setResetToken] = useState(searchParams.get('token') || '');

	useEffect(() => {
		// If the email or reset token are missing, redirect to the password reset page
		if (!email || !resetToken) {
			setToast({
				open: true,
				severity: 'error',
				message: 'Invalid password reset link. Please try again.',
				duration: 5000,
				clickToClose: true
			});
			navToPassResetPage();
		}

		// Verify the reset token
		verifyToken();

	}, [resetToken]);

	useEffect(() => {
		setEmail(searchParams.get('e') || '');
		setResetToken(searchParams.get('token') || '');
	}, [searchParams]);

	// Page State
	const [loading, setLoading] = useState(false);
	const [toast, setToast] = useState({
		open: false,
		severity: 'info',
		message: '',
		duration: 3000,
		clickToClose: false,
	});

	// Password Form State
	const [newPassword, setNewPassword] = useState('');
	const [newPassword2, setNewPassword2] = useState('');

	// Password conditions
	const [passwordLength, setPasswordLength] = useState(false);
	const [passwordUpper, setPasswordUpper] = useState(false);
	const [passwordLower, setPasswordLower] = useState(false);
	const [passwordNumber, setPasswordNumber] = useState(false);
	const [passwordSpecial, setPasswordSpecial] = useState(false);
	const [passwordNotEmail, setPasswordNotEmail] = useState(false);
	const [passwordMatch, setPasswordMatch] = useState(false);

	// Password requirements checks
	const checkPasswordLength = () => { setPasswordLength(validPasswordLength(newPassword)) };

	const checkPasswordUpper = () => { setPasswordUpper(validPasswordUppercase(newPassword)) };

	const checkPasswordLower = () => { setPasswordLower(validPasswordLowercase(newPassword)) };

	const checkPasswordNumber = () => { setPasswordNumber(validPasswordNumber(newPassword)) };
	
	const checkPasswordSpecial = () => { setPasswordSpecial(validPasswordSpecial(newPassword)) };

	const checkPasswordNotEmail = () => { setPasswordNotEmail(validPasswordNotEmail(newPassword, email)) };
	
	// Password match check
	const checkPasswordMatch = () => { setPasswordMatch(newPassword && newPassword2 && newPassword === newPassword2) };

	// Verify all password requirements are met
	const verifyPassRequirementsMet = passwordLength && passwordUpper && passwordLower && passwordNumber && passwordSpecial && passwordNotEmail;

	// Check if new password meets all requirements
	useEffect(() => {
		checkPasswordLength();
		checkPasswordUpper();
		checkPasswordLower();
		checkPasswordNumber();
		checkPasswordSpecial();
		checkPasswordNotEmail();
	}, [newPassword]);

	// Check if both new password entries match
	useEffect(() => {
		checkPasswordMatch();
	}, [newPassword, newPassword2]);

	// Navigate to the specified next page, or the locations list, after successful login
	const navToLoginPage = () => navigate('/login');

	// Navigate to the specified next page, or the locations list, after successful login
	const navToPassResetPage = () => navigate('/passwordreset');
	
	// Handle verifying a password reset token
	function verifyToken() {
		setLoading(true);
		axiosInstance.post(`password_reset/validate_token/`, {
				token: resetToken,
			})
			.then((res) => {
				if (res.status !== 200) {
					setToast({
						open: true,
						severity: 'error',
						message: 'Invalid password reset token. Please try password reset again.',
						duration: 5000,
						clickToClose: true
					});
					navToPassResetPage();
				}
			})
			.catch((error) => {
				setToast({
					open: true,
					severity: 'error',
					message: 'Invalid password reset token. Please try password reset again.',
					duration: 5000,
					clickToClose: true
				});
				console.error('Error:', error);
				navToPassResetPage();
			})
			.finally(() => {
				setLoading(false);
			});
	}

	// Handle setting a new password
	function resetPassword() {
		setLoading(true);
		axiosInstance.post(`password_reset/confirm/`, {
				token: resetToken,
				password: newPassword,
			})
			.then((res) => {
				setToast({
					open: true,
					severity: 'success',
					message: 'Password reset successful.',
					clickToClose: true,
				});
				navToLoginPage();
			})
			.catch((error) => {
				setToast({
					open: true,
					severity: 'error',
					message: 'Password reset failed. ' + error,
					clickToClose: true,
				});
				console.log(`Password reset error: ${error}`);
			})
			.finally(() => {
				setLoading(false);
			});
	}
	
	// Save Password button handler
	const handleSaveClick = () => {
		if (verifyPassRequirementsMet && passwordMatch) {
			resetPassword();
		} else if (!verifyPassRequirementsMet) {
			setToast({
				open: true,
				severity: 'error',
				message: 'Your new password does not meet minimum password requirements.',
				clickToClose: true,
			});
		} else if (!passwordMatch) {
			setToast({
				open: true,
				severity: 'error',
				message: 'Passwords do not match.',
				clickToClose: true,
			});
		}
	};

	// Cancel button handler
	const handleCancelClick = () => {
		navToLoginPage();
	};

	// Handle the new password form state update
	const handleNewPasswordChange = (event) => {
		setNewPassword(event.target.value);
	};

	// Handle the new password confirmation form state update
	const handleNewPassword2Change = (event) => {
		setNewPassword2(event.target.value);
	};

	return (		
		<Grid container component='main' sx={{ height: '100vh' }}>
			<CssBaseline />
			<Toast
				severity={toast.severity}
				message={toast.message}
				toastOpen={toast.open}
				setToastOpen={() => setToast({ ...toast, open: false })}
				duration={toast.duration}
				clickToClose={toast.clickToClose}
			/>
			<Backdrop
				sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
				open={loading}
			>
				<CircularProgress color="inherit" />
			</Backdrop>
			<Grid item xs={false} sm={4} md={6} lg={8} sx={{
				backgroundImage: `url(${bgimage})`,
				backgroundRepeat: 'no-repeat',
				backgroundColor: (t) =>
				t.palette.mode === 'light' ? t.palette.grey[50] : t.palette.grey[900],
				backgroundSize: 'cover',
				backgroundPosition: '5%',
			}} />
			<Grid item xs={12} sm={8} md={6} lg={4} component={Paper} elevation={6} square>
				<Box
					sx={{
						my: 8,
						mx: 4,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
					}}
				>
					<Avatar sx={{ m: 1, bgcolor: '#B1282E' }}>
						<PinIcon />
					</Avatar>
					<Typography component='h1' variant='h5' sx={{ mb: 2 }}>
						Set New Password
					</Typography>
					<Typography variant='body1' component='div' sx={{ textAlign: 'center' }}>
						Please enter a new password for your OBVM2 account.
					</Typography>
					<Box
						sx={{
							mt: 2,
							p: 3,
							border: '5px solid #B1282E',
							borderRadius: 3,
							width: '75%',
							minWidth: '400px',
						}}
					>
						<Grid container spacing={1}>
							<Grid item xs={12}>
								<TextField
									margin="dense"
									id="new_password"
									label="New Password"
									type="password"
									fullWidth
									error={newPassword.length > 0 && !verifyPassRequirementsMet}
									helperText={(newPassword.length > 0 && !verifyPassRequirementsMet) ? 'Password does not meet minimum requirements' : ''}
									value={newPassword}
									onChange={handleNewPasswordChange}
									inputProps={{ maxLength: 32 }}
									autoFocus
								/>
								<Box sx={{ mx: '20px', mt: '20px' }}>
									<Typography variant="h7" component="div" sx={{ flexGrow: 1 }}>
										Password Requirements:
									</Typography>
									<List>
										<ListItem sx={{ paddingY: 0 }}>
											{passwordLength ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
											<ListItemText sx={{ paddingLeft: 2 }} primary="At least 8 characters long" />
										</ListItem>
										<ListItem sx={{ paddingY: 0 }}>
											{passwordUpper ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
											<ListItemText sx={{ paddingLeft: 2 }} primary="At least 1 uppercase letter" />
										</ListItem>
										<ListItem sx={{ paddingY: 0 }}>
											{passwordLower ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
											<ListItemText sx={{ paddingLeft: 2 }} primary="At least 1 lowercase letter" />
										</ListItem>
										<ListItem sx={{ paddingY: 0 }}>
											{passwordNumber ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
											<ListItemText sx={{ paddingLeft: 2 }} primary="At least 1 number" />
										</ListItem>
										<ListItem sx={{ paddingY: 0 }}>
											{passwordSpecial ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
											<ListItemText sx={{ paddingLeft: 2 }} primary="At least 1 special character" />
										</ListItem>
										<ListItem sx={{ paddingY: 0 }}>
											{passwordNotEmail ? <CheckCircleIcon color="success" /> : <CancelIcon color="error" />}
											<ListItemText sx={{ paddingLeft: 2 }} primary="Not your email" />
										</ListItem>
									</List>
								</Box>
								<TextField
									margin="dense"
									id="new_password2"
									label="Confirm New Password"
									type="password"
									fullWidth
									error={newPassword2.length > 0 && !passwordMatch}
									helperText={(newPassword2.length > 0 && !passwordMatch) ? 'Passwords do not match' : ''}
									value={newPassword2}
									onChange={handleNewPassword2Change}
									inputProps={{ maxLength: 32 }}
								/>
							</Grid>
						</Grid>
					</Box>
					<Box sx={{ padding: 3 }}>
						<Button onClick={handleSaveClick} variant='contained' size='large' disabled={!verifyPassRequirementsMet || !passwordMatch} sx={{ mx: 1 }}>
							Set New Password
						</Button>
					</Box>
					<Link onClick={handleCancelClick} variant='body2' style={{ cursor: 'pointer' }}>
						Back to Login
					</Link>
				</Box>
			</Grid>
		</Grid>
	);
}